import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ElementRenderer from './ElementRenderer';
import { useSearchTerm } from '../SearchContext';
import { IconButton } from '@mui/material';
import { flattenKeys } from '../../helpers/DataDefinitions'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux';
import { useYObject } from '../../hooks/documentHooks';
import { getPropertyName } from '../../helpers/utils';
import BaseElement from './BaseElement';

const ObjectMutator = ({ definition, value, path, parent, itemKey, buttons, parentDefinition, dataTranslation, dataTranslationMap }) => {
  const newData = useYObject(value)
  const [open, setOpen] = useState(!parent)
  const { foundItems } = useSearchTerm()
  const title = useMemo(() => getPropertyName(definition?.title, itemKey || 'General'), [definition, itemKey])
  const dispatch = useDispatch()

  const objectDefinition = useMemo(() => {
    if (definition?.keys) {
      return flattenKeys(definition.keys, newData)
    } else {
      return null
    }
  }, [definition, newData])

  const customEditor = useMemo(() => {
    if (!definition?.editor) return false
    if (typeof definition.editor === 'string') return definition.editor
    if (typeof definition.editor === 'function') return definition.editor(newData)
    throw new Error('Invalid editor value')
  }, [definition, newData])

  const toggleOpen = useCallback(() => {
    setOpen(!open)
  }, [open])

  const handleCreate = useCallback((event) => {
    if (definition?.create?.templates) {
      dispatch({
        type: 'insert-template-modal',
        templates: definition.create.templates.sort((curr, next) => curr.title > next.title ? 1 : curr.title === next.title ? 0 : -1),
        object: value,
        path
      })
    }
    event.stopPropagation()
  }, [dispatch, definition, value, path])

  const handleEdit = useCallback((event) => {
    dispatch({
      type: 'edit-json',
      data: value
    })
    setOpen(false)
    event.stopPropagation()
  }, [value, dispatch])

  const handleOpenEditor = useCallback((event) => {
    dispatch({
      type: 'custom-editor',
      editor: customEditor,
      data: value
    })
    setOpen(false)
    event.stopPropagation()
  }, [value, customEditor, dispatch])

  useEffect(() => {
    if (foundItems && Object.keys(foundItems).length > 0) {
      const found = Object.keys(foundItems).find(item => item.startsWith(path))
      setOpen(!!found)
    }
  }, [foundItems, path])

  return (
    <BaseElement error={!definition}>
      <header-area onClick={toggleOpen}>
        <span>{title}<small>{parentDefinition?.children_title_key && <><br/>{`${newData[parentDefinition?.children_title_key]}`}</>}</small></span>
        <div>
          {buttons}
          {definition && definition.create && !dataTranslation && <IconButton size="small" onClick={handleCreate}><AddCircleOutlineIcon /></IconButton>}
          {customEditor && <IconButton size="small" onClick={handleOpenEditor}><AppRegistrationIcon /></IconButton>}
          {!dataTranslation && <IconButton size="small" onClick={handleEdit}><EditIcon /></IconButton>}
        </div>
      </header-area>
      {open && Object.entries(newData).map(([key, subvalue]) =>
        <section-content key={key}>
          <ElementRenderer
            path={parent ? `${path}.${key}` : key}
            value={subvalue}
            parent={value} 
            itemKey={key}
            definition={(objectDefinition && objectDefinition[key]) || definition?.items}
            parentDefinition={definition}
            dataTranslation={dataTranslation}
            dataTranslationMap={dataTranslationMap}
          />
        </section-content>
       )}
    </BaseElement>
  )
}

export default ObjectMutator